import { patch } from "@/infra/rest";
import { getSignedUrl } from "@/shared/requests/get-signed-url";

/**
 * Question structure from JSON file
 */
export interface IntakeFormQuestion {
  id: string; // The unique ID from backend
  question_number: number; // Used for display and as unique key
  question: string;
  field_name: string;
  question_type: "free_text" | "single_choice" | "multiple_choice" | "file_upload";
  required: boolean;
  is_dropdown: boolean;
  hidden_from_user: boolean;
  response_choices?: string[];
  max_number_of_files?: number; // Note: backend uses max_number_of_files, not max_files
  source_urls?: string[];
  agent_session_id?: string;
  response?: string | string[] | null;
  saveFailed?: boolean; // Track if the auto-save failed for this question
}

/**
 * Request payload for submitting a question response
 * Matches backend API schema exactly
 */
export interface SubmitQuestionResponsePayload {
  id: string;
  question: string;
  field_name: string;
  question_type: "free_text" | "single_choice" | "multiple_choice" | "file_upload";
  required: boolean;
  is_dropdown: boolean;
  hidden_from_user: boolean;
  response_choices: string[];
  question_number: number;
  max_number_of_files: number;
  source_urls: string[];
  agent_session_id: string;
  response: string | string[];
}

/**
 * Response from submitting a question
 */
export interface SubmitQuestionResponseResult {
  success: boolean;
  data: IntakeFormQuestion;
}

/**
 * Fetch questions from JSON URL with automatic URL refresh if expired
 * Similar to downloadFileWithCheck - checks if URL is valid, refreshes if needed
 */
export const fetchIntakeFormQuestions = async (
  jsonUrl: string
): Promise<IntakeFormQuestion[]> => {
  let currentUrl = jsonUrl;

  try {
    console.log("Fetching questions from:", currentUrl);

    // Try HEAD request to check if URL is valid
    try {
      const headResponse = await fetch(currentUrl, { method: "HEAD" });
      if (!headResponse.ok) {
        // If HEAD response is not OK, treat URL as stale
        throw new Error("Stale URL detected via HEAD response");
      }
    } catch (headError) {
      // HEAD request failed (network error, CORS, or stale URL)
      // Try to get a refreshed signed URL
      console.log("URL appears to be stale, attempting to refresh...");
      try {
        const signedUrls = await getSignedUrl({
          stale_urls: [currentUrl],
          max_age: 86400, // 24 hours
        });
        if (!signedUrls || signedUrls.length === 0) {
          throw new Error("Failed to refresh signed URL");
        }
        currentUrl = signedUrls[0];
        console.log("Successfully refreshed URL");
      } catch (refreshError) {
        console.error("Failed to refresh URL:", refreshError);
        throw refreshError;
      }
    }

    // Now try to fetch the questions using the valid/refreshed URL
    const response = await fetch(currentUrl);
    console.log("Fetch response status:", response.status, response.statusText);

    if (!response.ok) {
      throw new Error(`Failed to fetch questions: ${response.statusText}`);
    }

    const data = await response.json();
    console.log("Raw JSON data:", data);

    // Handle different response formats
    // 1. If data is an array directly
    if (Array.isArray(data)) {
      console.log("Data is an array, returning directly");
      return data;
    }

    // 2. If data has a 'questions' property
    if (data.questions && Array.isArray(data.questions)) {
      console.log("Data has questions property, returning data.questions");
      return data.questions;
    }

    // 3. If data has a 'data' property with questions
    if (data.data && Array.isArray(data.data)) {
      console.log("Data has data property, returning data.data");
      return data.data;
    }

    // If none of the above, return empty array
    console.warn("Could not find questions array in response, returning empty array");
    return [];
  } catch (error) {
    console.error("Error fetching intake form questions:", error);
    throw error;
  }
};

/**
 * Submit a single question response
 */
export const submitQuestionResponse = async (
  agentSessionId: string,
  questionId: string,
  payload: SubmitQuestionResponsePayload
): Promise<SubmitQuestionResponseResult> => {
  return patch<SubmitQuestionResponseResult>({
    url: `/v0/tpra/vendor-intake-form/${agentSessionId}/questions/${questionId}`,
    config: {
      data: { question: payload },
    },
    isAuthRequired: true,
  });
};
